import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './PlannerAdmin.css';

interface WeatherTip {
  id: number;
  title: string;
  animal_category: string;
  animal_category_display: string;
  production_system: string;
  production_system_display: string;
  life_stage: string;
  life_stage_display: string;
  weather_condition: string;
  weather_condition_display: string;
  severity: string;
  severity_display: string;
  advice: string;
  feed_note: string;
  emergency_signs: string;
  is_active: boolean;
  order: number;
}

interface TipFormData {
  title: string;
  animal_category: string;
  production_system: string;
  life_stage: string;
  weather_condition: string;
  severity: string;
  advice: string;
  feed_note: string;
  emergency_signs: string;
  is_active: boolean;
  order: number;
}

const defaultFormData: TipFormData = {
  title: '',
  animal_category: 'all',
  production_system: 'all',
  life_stage: 'all',
  weather_condition: 'heat',
  severity: 'moderate',
  advice: '',
  feed_note: '',
  emergency_signs: '',
  is_active: true,
  order: 0
};

const WeatherTipsAdmin: React.FC = () => {
  const [tips, setTips] = useState<WeatherTip[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [conditionFilter, setConditionFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingTip, setEditingTip] = useState<WeatherTip | null>(null);
  const [formData, setFormData] = useState<TipFormData>(defaultFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTips();
  }, [categoryFilter, conditionFilter]);

  const fetchTips = async () => {
    try {
      const params: Record<string, string> = {};
      if (categoryFilter !== 'all') params.animal_category = categoryFilter;
      if (conditionFilter !== 'all') params.weather_condition = conditionFilter;

      const response = await api.get('/planner/admin/weather-tips/', { params });
      setTips(response.data);
    } catch (error) {
      console.error('Failed to load tips:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTip(null);
    setFormData(defaultFormData);
    setShowForm(true);
  };

  const handleEdit = (tip: WeatherTip) => {
    setEditingTip(tip);
    setFormData({
      title: tip.title,
      animal_category: tip.animal_category,
      production_system: tip.production_system,
      life_stage: tip.life_stage,
      weather_condition: tip.weather_condition,
      severity: tip.severity,
      advice: tip.advice,
      feed_note: tip.feed_note,
      emergency_signs: tip.emergency_signs,
      is_active: tip.is_active,
      order: tip.order
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingTip) {
        await api.put(`/planner/admin/weather-tips/${editingTip.id}/`, formData);
      } else {
        await api.post('/planner/admin/weather-tips/', formData);
      }
      setShowForm(false);
      setEditingTip(null);
      fetchTips();
    } catch (error) {
      console.error('Failed to save tip:', error);
      alert('Failed to save tip');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tipId: number) => {
    if (!window.confirm('Are you sure you want to delete this tip?')) return;

    try {
      await api.delete(`/planner/admin/weather-tips/${tipId}/`);
      fetchTips();
    } catch (error) {
      console.error('Failed to delete tip:', error);
      alert('Failed to delete tip');
    }
  };

  const handleToggleActive = async (tip: WeatherTip) => {
    try {
      await api.patch(`/planner/admin/weather-tips/${tip.id}/`, {
        is_active: !tip.is_active
      });
      fetchTips();
    } catch (error) {
      console.error('Failed to toggle tip status:', error);
    }
  };

  if (loading) {
    return <div className="planner-admin-loading">Loading weather tips...</div>;
  }

  return (
    <div className="tips-admin-page">
      <div className="page-header">
        <h1>Weather Tips</h1>
        <div>
          <Link to="/planner" className="btn-secondary" style={{ marginRight: '12px' }}>
            ← Back
          </Link>
          <button className="btn-primary" onClick={handleCreate}>
            + Add Tip
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="all">All Animals</option>
          <option value="poultry_meat">Poultry - Meat</option>
          <option value="poultry_layer">Poultry - Layers</option>
          <option value="swine">Swine</option>
          <option value="goat">Goat</option>
          <option value="sheep">Sheep</option>
          <option value="rabbit">Rabbit</option>
        </select>
        <select value={conditionFilter} onChange={(e) => setConditionFilter(e.target.value)}>
          <option value="all">All Conditions</option>
          <option value="heat">Heat</option>
          <option value="cold">Cold</option>
          <option value="wet">Wet</option>
          <option value="storm">Storm</option>
          <option value="drought">Drought</option>
        </select>
      </div>

      <div className="tips-list">
        {tips.map(tip => (
          <div key={tip.id} className={`tip-card ${!tip.is_active ? 'inactive' : ''}`}>
            <div className="tip-header">
              <h3>{tip.title}</h3>
              <div className="tip-badges">
                <span className={`condition-badge ${tip.weather_condition}`}>
                  {tip.weather_condition_display}
                </span>
                <span className={`severity-badge ${tip.severity}`}>
                  {tip.severity_display}
                </span>
                <span className="category-badge">
                  {tip.animal_category_display}
                </span>
              </div>
            </div>

            <div className="tip-content">
              <p className="tip-advice">{tip.advice}</p>
              {tip.feed_note && (
                <div className="tip-section">
                  <strong>Feed Note:</strong> {tip.feed_note}
                </div>
              )}
              {tip.emergency_signs && (
                <div className="tip-section emergency">
                  <strong>Watch For:</strong> {tip.emergency_signs}
                </div>
              )}
            </div>

            <div className="tip-footer">
              <span className={`status-indicator ${tip.is_active ? 'active' : 'inactive'}`}>
                {tip.is_active ? 'Active' : 'Inactive'}
              </span>
              <div className="tip-actions">
                <button className="btn-small edit" onClick={() => handleEdit(tip)}>
                  Edit
                </button>
                <button
                  className={`btn-small ${tip.is_active ? 'warning' : 'success'}`}
                  onClick={() => handleToggleActive(tip)}
                >
                  {tip.is_active ? 'Disable' : 'Enable'}
                </button>
                <button className="btn-small danger" onClick={() => handleDelete(tip.id)}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}

        {tips.length === 0 && (
          <p className="empty-message">No tips found matching your filters</p>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <h2>{editingTip ? 'Edit Weather Tip' : 'Create Weather Tip'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Animal Category</label>
                  <select
                    value={formData.animal_category}
                    onChange={e => setFormData({...formData, animal_category: e.target.value})}
                  >
                    <option value="all">All Animals</option>
                    <option value="poultry_meat">Poultry - Meat</option>
                    <option value="poultry_layer">Poultry - Layers</option>
                    <option value="swine">Swine</option>
                    <option value="goat">Goat</option>
                    <option value="sheep">Sheep</option>
                    <option value="rabbit">Rabbit</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Weather Condition *</label>
                  <select
                    value={formData.weather_condition}
                    onChange={e => setFormData({...formData, weather_condition: e.target.value})}
                    required
                  >
                    <option value="heat">Heat</option>
                    <option value="cold">Cold</option>
                    <option value="wet">Wet</option>
                    <option value="storm">Storm</option>
                    <option value="drought">Drought</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Severity</label>
                  <select
                    value={formData.severity}
                    onChange={e => setFormData({...formData, severity: e.target.value})}
                  >
                    <option value="moderate">Moderate</option>
                    <option value="severe">Severe</option>
                    <option value="extreme">Extreme</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Advice *</label>
                <textarea
                  value={formData.advice}
                  onChange={e => setFormData({...formData, advice: e.target.value})}
                  rows={3}
                  required
                />
              </div>

              <div className="form-group">
                <label>Feed Note</label>
                <textarea
                  value={formData.feed_note}
                  onChange={e => setFormData({...formData, feed_note: e.target.value})}
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Emergency Signs to Watch For</label>
                <textarea
                  value={formData.emergency_signs}
                  onChange={e => setFormData({...formData, emergency_signs: e.target.value})}
                  rows={2}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.order}
                    onChange={e => setFormData({...formData, order: e.target.value as any})}
                    onBlur={e => {
                      const val = parseInt(e.target.value) || 0;
                      setFormData({...formData, order: Math.max(0, val)});
                    }}
                  />
                </div>

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={e => setFormData({...formData, is_active: e.target.checked})}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingTip ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherTipsAdmin;
